/**
 * My tasks: the day brief, and a filterable list underneath it.
 *
 * Two views, one query string, so either is a link somebody can paste.
 *
 * Contract rules, enforced here and at the server:
 *  1. Badges come from `counts.*`, never `overdue.length` - the lists are
 *     capped by `limit`/`truncated`; the counts are a separate exact query.
 *  2. The brief is ALWAYS the caller's own queue. A task set for a colleague
 *     shows in `GET /tasks`, never in the brief - the audit grant does not
 *     widen it, so there is no "everyone" control here.
 *  3. The audit is gated on the GRANT (`ability.hasAudit('tasks')`), never on
 *     `ability.isAdmin` - the grant can be given to or withdrawn from anyone,
 *     independent of role.
 *  4. `?assignedToId=` without the audit is a 403, not a narrowed list, so it
 *     is sent only when `hasAudit` is true. A 403 carries no `details`, so it
 *     renders as a banner, never a field error. `/tasks` is unusual among this
 *     API's list endpoints: the server itself defaults to the caller's own
 *     tasks, the opposite of the usual pattern here (server returns
 *     everything, client filters by ownerId) - don't copy that pattern onto
 *     this endpoint.
 *  5. `horizonDays` moves ONLY the `upcoming` bucket; nothing overdue is
 *     hidden by it.
 *  6. Due times render on the ASSIGNEE's clock via `formatDue`; every
 *     comparison is on `due.utc` - `due.local` is a rendering only and must
 *     never be sorted or compared on.
 *  7. "Overdue" is judged against a SKEW-CORRECTED clock (`brief.now` vs the
 *     local clock), measured once per load. Display only - no timers, no chime.
 *  8. An undated task is normal, not a fault.
 *  9. `state` and `closed` are mutually exclusive and driven by ONE control -
 *     the API refuses both at once.
 *
 * No `<main>` and no page header: `AppShell` owns those.
 */
import { Fragment, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronIcon, PlusIcon } from '@/components/catalog/icons';
import { TaskFormModal } from '@/components/tasks/TaskFormModal';
import { TaskList } from '@/components/tasks/TaskList';
import { Notice } from '@/components/ui/Notice';
import { ViewToggle } from '@/components/ui/ViewToggle';
import { ApiError, api } from '@/lib/api';
import { cx } from '@/lib/cx';
import { NAV_SURFACE_MODULE, denialReason } from '@/lib/permissions';
import { taskKindLabel, taskStateLabel, viewerZone } from '@/lib/task-time';
import type { Paginated, Task, TaskBrief, TaskKind, TaskListParams, TaskState } from '@/lib/types';
import { useAuth } from '@/lib/use-auth';
import { useUserOptions } from '@/lib/use-user-options';
import {
  Button,
  Checkbox,
  Combobox,
  EmptyState,
  MultiCombobox,
  Field,
  Panel,
  PageHeader,
  PanelFooter,
  Skeleton,
} from '@/components/ui';

/** The endpoint's own default, sent explicitly so the page size is ours rather than inherited. */
const PER_PAGE = 50;

const LIST_SKELETON_ROWS = 5;

/* ------------------------------------------------------------------ *
 * The query string, and nothing trusted out of it.
 *
 * Every parser below treats an unusable value as ABSENT rather than forwarding
 * it: a hand-edited `?horizon=99` is a 422 at the server, and a request that
 * cannot succeed is worse than a control that reverts to its default and shows
 * what it is set to.
 * ------------------------------------------------------------------ */

type TasksView = 'brief' | 'list';

/**
 * The URL parameters only **All tasks** can express.
 *
 * ONE list, read by three things that would otherwise drift: the view
 * resolution below, the switch back to My day, and Clear filters.
 */
const LIST_ONLY_PARAMS = ['status', 'kind', 'unattached', 'upcoming', 'who', 'sort', 'order'] as const;

function namesAListFilter(params: URLSearchParams): boolean {
  return LIST_ONLY_PARAMS.some((key) => params.has(key));
}

/**
 * Which half of this page a URL is asking for.
 *
 * 🔴 **A LINK THAT NAMES A FILTER OPENS THE VIEW THAT CAN SHOW IT**, and until
 * 2026-09-11 it did not. `/tasks?status=closed` landed on My day — which is
 * **open tasks only, by construction**: it renders `GET /tasks/brief`, whose
 * SQL is `t.state = 'open'`, and it mounts no status control at all. So the
 * parameter was parsed, ignored, and invisible: no request carried it, nothing
 * on screen said it was set, and the page answered *"Nothing outstanding"* to
 * somebody who had explicitly asked for their closed work.
 *
 * That is this app's own rule — **an active filter may never be invisible** —
 * and the owner's ruling recorded for saved views: *a link that names any
 * filter wins entirely*. It is also, measured, the report behind *"old and
 * completed tasks are not visible"* (Daryna, 4 September 2026): a completed
 * task is on the record's own drawer and is correct there, and on THIS page it
 * was reachable only by finding a toggle.
 *
 * ⚠️ **An explicit `view` still wins**, both ways round. The toggle writes
 * `view=list` and deletes the key for My day, so a hand-typed `view=brief`
 * beside a filter is a contradiction somebody typed on purpose; honouring it
 * costs nothing, and refusing it would make the parameter the one inert control
 * left on the page.
 */
function parseView(raw: string | null, params: URLSearchParams): TasksView {
  if (raw === 'list') return 'list';
  if (raw === 'brief') return 'brief';
  return namesAListFilter(params) ? 'list' : 'brief';
}

/*
 * 1-30 at the API, default 7. A hand-typed value inside the range is kept and
 * added to the options, so a shared link with a five-day horizon renders one
 * instead of snapping to 7.
 */
const HORIZON_CHOICES = [1, 3, 7, 14, 30];
const DEFAULT_HORIZON = 7;

function parseHorizon(raw: string | null): number {
  if (!raw) return DEFAULT_HORIZON;
  const value = Number(raw);
  return Number.isInteger(value) && value >= 1 && value <= 30 ? value : DEFAULT_HORIZON;
}

/**
 * ONE multi-select over two mutually exclusive params: `state` and `closed`
 * can't both travel (rule 9). Multi-value since 2026-10-28 (owner: every
 * filter takes several values).
 *
 * `?status=` is a CSV of states. Absent = open only (the page's default, not
 * the server's); `all` = any state. The pre-2026-10-28 single values still
 * parse: `closed` = done + cancelled, `all`, and each state on its own.
 */
const TASK_STATES: readonly TaskState[] = ['open', 'done', 'cancelled'];

function parseStatuses(raw: string | null): TaskState[] {
  if (raw === null || raw === '') return ['open'];
  const picked = new Set<TaskState>();
  for (const token of raw.split(',')) {
    const value = token.trim();
    if (value === 'all') return [...TASK_STATES];
    if (value === 'closed') {
      picked.add('done');
      picked.add('cancelled');
      continue;
    }
    const state = TASK_STATES.find((candidate) => candidate === value);
    if (state) picked.add(state);
  }
  return picked.size === 0 ? ['open'] : TASK_STATES.filter((state) => picked.has(state));
}

/*
 * ── THE FOUR FILTER VOCABULARIES, AS DATA ─────────────────────────────────
 *
 * ⚠️ **Built here rather than as JSX children, because the control is a
 * `Combobox` now** (owner's decision 2026-09-13: every non-table-column filter
 * dropdown is searchable, whatever its option count). A native `<Select>` takes
 * `<option>` elements; a `Combobox` takes an ARRAY — so the list has to exist
 * as a value somewhere, and module scope is the one place it costs nothing:
 * these four are constant, so a fresh array per render would churn the
 * popover's `visible` memo and its measure pass for no gain.
 *
 * ⚠️ **`STATUS_OPTIONS` is DERIVED from `TASK_STATES`, deliberately.**
 * `TASK_STATES` is what `parseStatuses` accepts out of the URL; a hand-written
 * option list beside it is a second vocabulary that drifts silently.
 * SUPERSEDED 2026-10-28: "Closed (done or cancelled)" and "Any status" rows —
 * ticking Done and Cancelled is closed, and an empty selection is any status.
 */
const STATUS_OPTIONS = TASK_STATES.map((value) => ({ value, label: taskStateLabel(value) }));

/** The trigger's text for an empty selection, which means any state. */
const ANY_STATUS_LABEL = 'Any status';

/** Presentational only - no kind moves a stage or triggers anything. */
const KINDS: TaskKind[] = ['follow_up', 'call', 'email', 'meeting', 'document', 'review'];

/** No "Any kind" row: an empty selection is any kind (the trigger's placeholder). */
const KIND_OPTIONS = KINDS.map((value) => ({ value, label: taskKindLabel(value) }));

/** `?kind=` is a CSV of kinds (2026-10-28); a single old value still parses. Absent = any kind. */
function parseKinds(raw: string | null): TaskKind[] {
  if (!raw) return [];
  const picked = new Set(raw.split(',').map((token) => token.trim()));
  return KINDS.filter((kind) => picked.has(kind));
}

const SORTS = ['dueAt', 'createdAt', 'title'] as const;
type TaskSort = (typeof SORTS)[number];
type TaskOrder = 'asc' | 'desc';

/**
 * The label on the empty choice — no `sort`, no `order`, the server's own
 * ordering.
 *
 * ⚠️ **It is NOT a member of `SORT_CHOICES` any more, and that is not a tidy-up.**
 * *SUPERSEDED 2026-09-13: `{ value: '', label: 'Default order' }` was the first
 * entry of that array, because a native `<Select>` has nowhere else to put an
 * empty `<option>`.* A `Combobox` draws the empty row itself from
 * `emptyOptionLabel` and pins it above every fetched row; leaving it in the
 * array as well renders it twice. The constant exists so the one string still
 * has one home — `emptyOptionLabel` and `placeholder` both read it, and the
 * placeholder is what the trigger falls back to when `value` matches no option.
 */
const DEFAULT_ORDER_LABEL = 'Default order';

/**
 * Sort and direction are one control writing both keys - a half-set pair
 * ("by title", no direction) is a request nothing on screen describes. An
 * empty value sends neither key and takes the server's own order.
 */
const SORT_CHOICES: { value: string; label: string }[] = [
  { value: 'dueAt:asc', label: 'Due soonest first' },
  { value: 'dueAt:desc', label: 'Due latest first' },
  { value: 'createdAt:desc', label: 'Newest first' },
  { value: 'createdAt:asc', label: 'Oldest first' },
  { value: 'title:asc', label: 'Title A-Z' },
  { value: 'title:desc', label: 'Title Z-A' },
];

function parseSort(raw: string | null): TaskSort | null {
  return SORTS.find((value) => value === raw) ?? null;
}

function parseOrder(raw: string | null): TaskOrder | null {
  return raw === 'asc' || raw === 'desc' ? raw : null;
}

/**
 * Whose queue the LIST is showing; the brief never consults this. A list of
 * tokens: `me` (the only value reachable without the audit grant) and
 * positive integers naming colleagues. The EMPTY list is everyone — the
 * ABSENCE of `assignedToId`.
 *
 * `?who=` is a CSV since 2026-10-28; absent = `['me']`, `everyone` = `[]`, and
 * an old single value parses as itself. A value with no usable token falls
 * back to `me`, as it always did.
 */
function parseWho(raw: string | null): string[] {
  if (raw === 'everyone') return [];
  const tokens: string[] = [];
  for (const token of (raw ?? '').split(',')) {
    const value = token.trim();
    if (value === 'me') {
      if (!tokens.includes('me')) tokens.push('me');
      continue;
    }
    const id = Number(value);
    if (value !== '' && Number.isInteger(id) && id > 0 && !tokens.includes(String(id))) {
      tokens.push(String(id));
    }
  }
  return tokens.length === 0 ? ['me'] : tokens;
}

function isOnlyMe(who: readonly string[]): boolean {
  return who.length === 1 && who[0] === 'me';
}

/* ------------------------------------------------------------------ *
 * The four buckets.
 * ------------------------------------------------------------------ */

type BucketKey = 'overdue' | 'today' | 'upcoming' | 'undated';

interface BucketSpec {
  key: BucketKey;
  label: string;
  /** Shown in place of the rows. An empty bucket is good news, never an error. */
  empty: string;
}

/*
 * ⚠️ **One short line per empty group** (owner, 2026-10-11: the page "has too
 * much text"). SUPERSEDED: "Nothing else is due today.", "Everything you hold
 * has a time on it." — and the four `StatTile`s plus a per-section count badge,
 * which said every count twice. The count bar above the groups is now the one
 * place each count is read.
 */
const BUCKETS: BucketSpec[] = [
  { key: 'overdue', label: 'Overdue', empty: 'Nothing overdue' },
  { key: 'today', label: 'Today', empty: 'Nothing due today' },
  { key: 'upcoming', label: 'Upcoming', empty: 'Nothing upcoming' },
  { key: 'undated', label: 'No date', empty: 'Nothing undated' },
];

/** My day | All tasks. Two options, both fit at 320px, and the pressed state is the message. */
const VIEW_OPTIONS: { value: TasksView; label: string }[] = [
  { value: 'brief', label: 'My day' },
  { value: 'list', label: 'All tasks' },
];

/**
 * The clock-skew line appears only when it is big enough to change what
 * "overdue" means for a reader planning their next hour. A minute or two is
 * noise, and a permanent sentence about it was most of what this page said.
 */
const SKEW_WARNING_MINUTES = 5;

/** The heading id each count in the bar jumps to. */
function groupHeadingId(key: BucketKey): string {
  return `tasks-group-${key}`;
}

/**
 * Scrolls to a group and puts focus on its heading, so a keyboard user lands
 * where a pointer user's eyes do. Smooth only when motion is welcome.
 */
function jumpToGroup(key: BucketKey) {
  const heading = document.getElementById(groupHeadingId(key));
  if (!heading) return;
  const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  heading.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  heading.focus({ preventScroll: true });
}

function ErrorBanner({ title, children }: { title: ReactNode; children?: ReactNode }) {
  return (
    <Notice tone="danger" role="alert" title={title} className="mx-4 my-3">
      {children && <span className="min-w-0 [overflow-wrap:anywhere]">{children}</span>}
    </Notice>
  );
}

export function TasksPage() {
  const { user, ability } = useAuth();

  /*
   * The read gate on the sidebar SURFACE, not on the task records.
   *
   * ⛔ **`trackers-my-tasks`, never `tasks`.** They look interchangeable and
   * answer different questions: `tasks` gates the RECORDS and is enforced
   * server-side, so denying it would blank the activity drawer on every company,
   * deal, quote and invoice in the product. This one gates one nav row and this
   * page, client-side only. `NAV_SURFACE_MODULE` says the same at the member.
   *
   * ⚠️ DEGRADES OPEN: only an explicit `none` scope denies. See `can()` in
   * `@/lib/permissions`.
   *
   * ⚠️ **Hiding a page is not refusing a request.** `GET /tasks` answers the
   * same to everybody it answered before, so nothing here may be worded as
   * though the queue were protected. This is a view control.
   *
   * ⚠️ Computed here rather than as an early return, because the return has to
   * sit below every hook. `hasAudit` and the create grant below are untouched.
   */
  const canReadSurface = ability.canRead(NAV_SURFACE_MODULE.trackerMyTasks);

  const [searchParams, setSearchParams] = useSearchParams();

  // Grant-gated (rule 3), never role-gated: never `ability.isAdmin`.
  const hasAudit = ability.hasAudit('tasks');

  /*
   * The WRITE grants, which are a different question from the audit above:
   * that one asks whose tasks may be READ, these ask what may be done to one.
   *
   * `/tasks` carries no `requireRole` — it is one of the three modules gated on
   * permission sets WITH a working scope — so these are the real answer and a
   * role check here would be a proxy for them. Asked separately because a set
   * can grant create and refuse delete.
   */
  const canCreateTask = ability.canCreate('tasks');
  const canUpdateTask = ability.canUpdate('tasks');
  const canDeleteTask = ability.canDelete('tasks');
  const createTaskDenial = canCreateTask ? null : denialReason(ability, 'create', 'tasks');
  const updateTaskDenial = canUpdateTask ? null : denialReason(ability, 'update', 'tasks');

  const viewerId = user?.id ?? null;
  const viewerTimeZone = viewerZone(user?.timezone);

  const view = parseView(searchParams.get('view'), searchParams);
  const horizonDays = parseHorizon(searchParams.get('horizon'));
  const statusesRaw = searchParams.get('status');
  const kindsRaw = searchParams.get('kind');
  const whoRaw = searchParams.get('who');
  // Memoised on the raw string so the arrays are stable across renders: they
  // feed `loadList`'s dependencies, and a fresh array each render would refetch.
  const statuses = useMemo(() => parseStatuses(statusesRaw), [statusesRaw]);
  const kinds = useMemo(() => parseKinds(kindsRaw), [kindsRaw]);
  const unattached = searchParams.get('unattached') === '1';
  // Snoozed follow-ups (`availableFrom` in the future) are hidden by the server
  // unless asked for. List only: the brief never sends it.
  const includeUpcoming = searchParams.get('upcoming') === '1';
  const sort = parseSort(searchParams.get('sort'));
  const order = parseOrder(searchParams.get('order'));
  // A `who` in the URL is ignored outright without the grant, so a pasted link
  // cannot put a request on the wire that could only ever come back 403.
  const who = useMemo(() => (hasAudit ? parseWho(whoRaw) : ['me']), [hasAudit, whoRaw]);

  // Decided once: null for everybody without the grant (sending it regardless
  // would 403, not narrow). A CSV of ids otherwise; `me` resolves to the
  // viewer and duplicates collapse. An empty `who` is everyone: no key.
  const assignedToId: string | null = useMemo(() => {
    if (!hasAudit || who.length === 0) return null;
    const ids: number[] = [];
    for (const token of who) {
      const id = token === 'me' ? viewerId : Number(token);
      if (id !== null && !ids.includes(id)) ids.push(id);
    }
    return ids.length === 0 ? null : ids.join(',');
  }, [hasAudit, who, viewerId]);

  const [brief, setBrief] = useState<TaskBrief | null>(null);
  /**
   * How many closed tasks this person has, counted ONLY when their day is
   * empty — and `null` for "we have not asked" or "the ask failed", never 0.
   *
   * ⚠️ **A door onto nothing is worse than no door**, so the control this feeds
   * renders on a real count and on nothing else. A failed count therefore reads
   * the same as none: silent. The alternative — offering "See completed tasks"
   * unconditionally — lands somebody on an empty list and teaches them the
   * filter is broken, which is the complaint this exists to answer.
   */
  const [closedCount, setClosedCount] = useState<number | null>(null);
  const [briefLoading, setBriefLoading] = useState(true);
  const [briefError, setBriefError] = useState<string | null>(null);

  const [result, setResult] = useState<Paginated<Task> | null>(null);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [listForbidden, setListForbidden] = useState(false);
  const [page, setPage] = useState(1);

  const [formState, setFormState] = useState<{ task?: Task } | null>(null);
  const [busyTaskId, setBusyTaskId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Bumped after every successful write; both loaders depend on it. The 250ms
  // debounce coalesces a run of ticked checkboxes into one refetch - the
  // limiter is per IP, shared office-wide.
  // SUPERSEDED: "120 requests/60s". It is 12,000/60s (corrected 2026-09-17).
  // Coalescing a run of ticks into one refetch is still right; the arithmetic
  // that reason cited was off by a factor of a hundred.
  const [refreshTick, setRefreshTick] = useState(0);
  const bumpRefresh = useCallback(() => setRefreshTick((n) => n + 1), []);

  // Gap between the server's clock and this machine's, measured once per
  // brief load from `brief.now` (rule 7). `null` means not yet measured;
  // helpers then fall back to the local clock.
  const [clockSkewMs, setClockSkewMs] = useState<number | null>(null);

  /** The corrected clock for `isOverdue` / `relativeDue`. Display only - no timers, no chime. */
  const correctedNow = useCallback((): Date | undefined => {
    if (clockSkewMs === null) return undefined;
    return new Date(Date.now() + clockSkewMs);
  }, [clockSkewMs]);

  /*
   * Fetched only for the audit picker, which only exists with the grant.
   *
   * 🔴 **`hasMore` WAS DISCARDED HERE UNTIL 2026-09-13, and the searchable
   * picker is what makes that untenable.** `/users` caps at 100 a page and this
   * reads one page, so a directory past the cap was already truncated — but a
   * native `<Select>` at least showed the reader the whole of what it had, and
   * a name missing from a scrolled list of ~100 is a name somebody keeps
   * scrolling for. A search box filters **only what is loaded**: typing a
   * colleague who sits past the cap now returns NOTHING, which reads as that
   * person having no tasks rather than as a list that stops. The footnote on
   * the control is the only thing that separates those two, so the flag has to
   * reach the render.
   */
  const {
    users,
    loading: usersLoading,
    failed: usersFailed,
    hasMore: moreUsers,
  } = useUserOptions(hasAudit && view === 'list');

  const loadBrief = useCallback(
    (signal?: AbortSignal) => {
      // `refreshTick` is the refetch trigger; the linter can't see it "used"
      // here and flags it unnecessary - deleting it silently breaks every
      // reload after a write.
      void refreshTick;
      setBriefLoading(true);
      return api
        .taskBrief({ horizonDays }, signal)
        .then((body) => {
          if (signal?.aborted) return;
          setBrief(body.data);
          const serverNow = Date.parse(body.data.now);
          setClockSkewMs(Number.isNaN(serverNow) ? null : serverNow - Date.now());
          setBriefError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setBriefError(caught instanceof Error ? caught.message : 'Failed to load your day');
        })
        .finally(() => {
          if (!signal?.aborted) setBriefLoading(false);
        });
    },
    [horizonDays, refreshTick],
  );

  /*
   * 🔴 **"NOTHING OUTSTANDING" IS A TRUE SENTENCE THAT GETS READ AS A FALSE ONE.**
   *
   * My day is open-only by construction — `GET /tasks/brief` is `t.state =
   * 'open'` server-side — so somebody who has finished everything sees an empty
   * page whose description reads *"Add a task to hand work to somebody."* That
   * is exactly right about today and exactly wrong about the five completed
   * tasks sitting behind it, and it is the sentence behind *"old and completed
   * tasks are not visible"* (Daryna, 4 September 2026).
   *
   * So when, and ONLY when, the day is empty, ask how many closed ones there
   * are. One request, on the one screen where the question is being asked.
   *
   * ⚠️ **The count and the destination must be the SAME QUERY or the door lies.**
   * `assignedToId` is sent here exactly when `loadList` sends it — with the
   * audit grant and never without, because without it the parameter is a 403
   * rather than a narrowing — and the button below navigates to the list with
   * `who` left at its default of *me*, which is what resolves to that same id.
   * A count taken one way and a list rendered another is how a badge comes to
   * disagree with the page it opens.
   */
  useEffect(() => {
    /*
     * ⚠️ **Returns without clearing, deliberately — nothing here writes state
     * synchronously in an effect.** A stale count cannot be shown: the only
     * thing that reads it is the empty-day branch, which is the same condition
     * this effect fires on, so a day that has since filled up simply never
     * consults it and this effect runs again the moment it empties.
     */
    if (view !== 'brief' || brief === null || brief.counts.total !== 0) return;
    const controller = new AbortController();
    const params: TaskListParams = { closed: true, perPage: 1 };
    if (hasAudit && viewerId !== null) params.assignedToId = viewerId;
    api
      .listTasks(params, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        setClosedCount(data.meta.total);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setClosedCount(null);
      });
    return () => controller.abort();
  }, [view, brief, hasAudit, viewerId, refreshTick]);

  const loadList = useCallback(
    (signal?: AbortSignal) => {
      // Same refetch trigger as the brief; see the note in `loadBrief`.
      void refreshTick;
      setListLoading(true);
      const params: TaskListParams = { page, perPage: PER_PAGE };
      // ONE of `state` or `closed`, never both (rule 9). Every state = no key.
      // Done + cancelled travels as `closed=true`, the one form every server
      // version has always accepted; any other set is a `state` CSV.
      if (statuses.length < TASK_STATES.length) {
        if (statuses.length === 2 && !statuses.includes('open')) params.closed = true;
        else params.state = statuses.join(',');
      }
      if (kinds.length > 0) params.kind = kinds.join(',');
      if (unattached) params.unattached = true;
      if (includeUpcoming) params.includeUpcoming = true;
      // Redundant with the null above by design: assignedToId would 403
      // without the grant, not narrow the list, so it's guarded again here.
      if (hasAudit && assignedToId !== null) params.assignedToId = assignedToId;
      if (sort) params.sort = sort;
      if (order) params.order = order;

      return api
        .listTasks(params, signal)
        .then((data) => {
          if (signal?.aborted) return;
          setResult(data);
          setListError(null);
          setListForbidden(false);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          // A 403 means the grant went away under a live token. No `details`,
          // so it renders as a banner - never as "nothing matched", which
          // would describe a refusal as an empty queue.
          if (caught instanceof ApiError && caught.status === 403) {
            setResult(null);
            setListForbidden(true);
            setListError(null);
            return;
          }
          setListForbidden(false);
          setListError(caught instanceof Error ? caught.message : 'Failed to load tasks');
        })
        .finally(() => {
          if (!signal?.aborted) setListLoading(false);
        });
    },
    [page, statuses, kinds, unattached, includeUpcoming, hasAudit, assignedToId, sort, order, refreshTick],
  );

  // The two views never fetch at the same time - a request for a view nobody
  // is looking at comes out of a colleague's share of the rate limit.
  useEffect(() => {
    if (view !== 'brief') return;
    const controller = new AbortController();
    const timer = setTimeout(() => void loadBrief(controller.signal), 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [loadBrief, view]);

  useEffect(() => {
    if (view !== 'list') return;
    const controller = new AbortController();
    const timer = setTimeout(() => void loadList(controller.signal), 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [loadList, view]);

  /* --- writing to the query string -------------------------------------- */

  function writeParams(mutate: (next: URLSearchParams) => void, resetPage = true) {
    if (resetPage) setPage(1);
    const next = new URLSearchParams(searchParams);
    mutate(next);
    setSearchParams(next, { replace: true });
  }

  function setParam(key: string, value: string) {
    writeParams((next) => {
      if (value === '') next.delete(key);
      else next.set(key, value);
    });
  }

  /** Open only = no key (the default); none or every state = `all`; else a CSV. */
  function setStatuses(values: string[]) {
    const picked = TASK_STATES.filter((state) => values.includes(state));
    writeParams((next) => {
      if (picked.length === 1 && picked[0] === 'open') next.delete('status');
      else if (picked.length === 0 || picked.length === TASK_STATES.length) next.set('status', 'all');
      else next.set('status', picked.join(','));
    });
  }

  function setKinds(values: string[]) {
    setParam('kind', values.join(','));
  }

  /** Just me = no key (the default); nobody ticked = `everyone`; else a CSV. */
  function setWho(values: string[]) {
    writeParams((next) => {
      if (values.length === 0) next.set('who', 'everyone');
      else if (isOnlyMe(values)) next.delete('who');
      else next.set('who', values.join(','));
    });
  }

  function setView(next: TasksView) {
    writeParams((params) => {
      if (next === 'list') {
        // Going the other way keeps everything: All tasks can show all of it.
        params.set('view', 'list');
        return;
      }
      params.delete('view');
      /*
       * ⚠️ **My day DROPS the filters it cannot express, and this is load-bearing
       * rather than tidiness.** `parseView` now reads a bare `?status=closed` as
       * a request for the list, so leaving one behind would bounce the toggle
       * straight back to All tasks and read as a broken control. Clearing them
       * is also the honest half on its own: My day mounts no status, kind, whose
       * or order control, so a value left in the URL is one nothing on screen
       * can show and nothing can reset.
       */
      for (const key of LIST_ONLY_PARAMS) params.delete(key);
    }, false);
  }

  /** All tasks, narrowed to done-or-cancelled — the destination the count above measured. */
  function showClosedTasks() {
    writeParams((next) => {
      next.set('view', 'list');
      next.set('status', 'closed');
      // Left alone deliberately: 'who' defaults to me, which is the population
      // the count was taken over.
    });
  }

  function setSortChoice(value: string) {
    const [nextSort = '', nextOrder = ''] = value.split(':');
    writeParams((next) => {
      if (nextSort && nextOrder) {
        next.set('sort', nextSort);
        next.set('order', nextOrder);
      } else {
        next.delete('sort');
        next.delete('order');
      }
    });
  }

  function clearListFilters() {
    writeParams((next) => {
      for (const key of LIST_ONLY_PARAMS) next.delete(key);
    });
  }

  /* --- optimistic writes ------------------------------------------------- */

  const patchTask = useCallback((id: number, replace: (task: Task) => Task) => {
    setBrief((previous) =>
      previous
        ? {
            ...previous,
            overdue: previous.overdue.map((task) => (task.id === id ? replace(task) : task)),
            today: previous.today.map((task) => (task.id === id ? replace(task) : task)),
            upcoming: previous.upcoming.map((task) => (task.id === id ? replace(task) : task)),
            undated: previous.undated.map((task) => (task.id === id ? replace(task) : task)),
          }
        : previous,
    );
    setResult((previous) =>
      previous
        ? { ...previous, data: previous.data.map((task) => (task.id === id ? replace(task) : task)) }
        : previous,
    );
  }, []);

  const removeTask = useCallback((id: number) => {
    setBrief((previous) =>
      previous
        ? {
            ...previous,
            overdue: previous.overdue.filter((task) => task.id !== id),
            today: previous.today.filter((task) => task.id !== id),
            upcoming: previous.upcoming.filter((task) => task.id !== id),
            undated: previous.undated.filter((task) => task.id !== id),
          }
        : previous,
    );
    setResult((previous) =>
      previous ? { ...previous, data: previous.data.filter((task) => task.id !== id) } : previous,
    );
  }, []);

  /**
   * Complete or re-open, optimistically, with the original row as rollback.
   * The optimistic patch changes `state` and NOTHING else - `completedBy` /
   * `completedAt` are stamped server-side, so inventing them locally would
   * show a name and time nobody recorded. They arrive with the response.
   */
  async function handleToggleState(task: Task, next: TaskState) {
    setActionError(null);
    setBusyTaskId(task.id);
    patchTask(task.id, (current) => ({ ...current, state: next }));
    try {
      const saved = await api.updateTask(task.id, { state: next });
      patchTask(task.id, () => saved.data);
      // Re-bucket and re-count from the server rather than decrementing
      // locally (rule 1).
      bumpRefresh();
    } catch (caught) {
      patchTask(task.id, () => task);
      setActionError(
        caught instanceof ApiError && caught.status === 403
          ? 'Your account is not allowed to change this task.'
          : caught instanceof Error
            ? caught.message
            : 'Could not update the task',
      );
    } finally {
      setBusyTaskId(null);
    }
  }

  /**
   * Delete, awaited rather than optimistic: only the creator (or an audit
   * holder) may, so the row should not vanish before the server agrees.
   */
  async function handleDelete(task: Task) {
    setActionError(null);
    setBusyTaskId(task.id);
    try {
      await api.deleteTask(task.id);
      removeTask(task.id);
      bumpRefresh();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 404) {
        // Already gone. Nothing to report - the view just catches up.
        removeTask(task.id);
        bumpRefresh();
      } else if (caught instanceof ApiError && caught.status === 403) {
        setActionError(
          'Only the person who asked for a task can delete it - close it instead.',
        );
      } else {
        setActionError(caught instanceof Error ? caught.message : 'Could not delete the task');
      }
    } finally {
      setBusyTaskId(null);
    }
  }

  function handleSaved() {
    const wasCreate = !formState?.task;
    setFormState(null);
    // A new task lands in whichever bucket it belongs to; the refetch decides,
    // rather than this page guessing which one from a wall clock it did not resolve.
    if (wasCreate && page !== 1) setPage(1);
    bumpRefresh();
  }

  /* --- derived ----------------------------------------------------------- */

  const counts = brief?.counts;

  /*
   * SUPERSEDED 2026-10-11 (owner: "too much text"): a "Next up" strip, a footer
   * saying times are the assignee's clock, and an "N overdue on this page"
   * clause in the pager. Each row's due chip now says when, its `title` names a
   * foreign clock in full, and the expanded row repeats it in words.
   */
  const skewMinutes = clockSkewMs === null ? 0 : Math.round(clockSkewMs / 60_000);

  // Rendered once per page render and handed to every compact row, so all of
  // them judge "late" against the same skew-corrected instant (rule 7).
  const now = correctedNow();

  const rows = result?.data ?? [];
  const total = result?.meta.total ?? 0;
  const totalPages = Math.max(result?.meta.totalPages ?? 1, 1);
  const firstRow = total === 0 ? 0 : (page - 1) * PER_PAGE + 1;
  const lastRow = total === 0 ? 0 : firstRow + rows.length - 1;
  const listInitialLoad = listLoading && !result;
  const briefInitialLoad = briefLoading && !brief;

  const anyListFilter =
    !(statuses.length === 1 && statuses[0] === 'open') ||
    kinds.length > 0 ||
    unattached ||
    includeUpcoming ||
    !isOnlyMe(who) ||
    sort !== null;

  const whoLabel =
    who.length === 0
      ? "everyone's tasks"
      : isOnlyMe(who)
        ? 'your tasks'
        : `tasks for ${who
            .map((token) =>
              token === 'me'
                ? 'you'
                : (users.find((row) => String(row.id) === token)?.name ?? `user #${token}`),
            )
            .join(', ')}`;

  const listDescription = listInitialLoad
    ? 'Loading...'
    : `${total} ${total === 1 ? 'task' : 'tasks'} - ${whoLabel}`;

  /*
   * ⚠️ **`horizonDays` is UNIONED IN, which is what makes the picker's
   * placeholder unreachable.** `parseHorizon` clamps anything unusable back to
   * 7 and anything usable through unchanged, and this adds whatever came
   * through to the offered set — so `value` is always one of these rows, and a
   * `Combobox` falling back to its placeholder over a real horizon cannot
   * happen. Deleting the union would not break the shared five-day link
   * loudly; it would render "Select…" over a board filtered to five days.
   */
  const horizonOptions = useMemo(
    () =>
      Array.from(new Set([...HORIZON_CHOICES, horizonDays]))
        .sort((a, b) => a - b)
        .map((days) => ({
          value: String(days),
          label: days === 1 ? 'Next 1 day' : `Next ${days} days`,
        })),
    [horizonDays],
  );

  /*
   * ── WHOSE TASKS: FOUR GROUPS IN ONE FLAT LIST ─────────────────────────────
   *
   * 🔴 **`Mine` and `Everyone's tasks` are STATIC and must stay reachable when
   * the staff read FAILS.** They are not people: `me` is the caller and
   * `everyone` is the ABSENCE of `assignedToId`, so neither needs `/users` to
   * have answered. They are unshifted here rather than fetched, which is what
   * makes a dead directory leave a control that still WORKS and merely appears
   * to know no colleagues — the `Field` error beside it says exactly that.
   *
   * 🔴 **The injected `User #N` row is load-bearing in a way it was not on a
   * `<Select>`.** A `?who=` deep link can name somebody off the loaded page —
   * past the 100-row cap, or simply not yet arrived. A native select silently
   * showed the first option in that case; a `Combobox` falls back to its
   * PLACEHOLDER, so without this row the control would read "Mine" (or blank)
   * while the list underneath it is filtered to a colleague. The URL, the
   * request and the panel description would all disagree with the picker, and
   * nothing would fail.
   *
   * The email rides as a `hint`, which is the option-object kind the
   * no-helper-text rule has never covered — it is what tells two colleagues
   * called the same thing apart, and it is also searchable, so "@" plus a
   * surname finds a person whose display name is a nickname.
   */
  /*
   * SUPERSEDED 2026-10-28 (multi-value filters): `Everyone’s tasks` is no
   * longer a row. It is the EMPTY selection — the control's placeholder — and
   * still needs nothing from `/users`. One injected `User #N` row per id in the
   * link that is not on the loaded page.
   */
  const whoOptions = useMemo(() => {
    const rows: { value: string; label: string; hint?: string }[] = [{ value: 'me', label: 'Mine' }];
    for (const token of who) {
      if (token !== 'me' && !users.some((row) => String(row.id) === token)) {
        rows.push({ value: token, label: `User #${token}` });
      }
    }
    for (const row of users) {
      rows.push({ value: String(row.id), label: row.name, hint: row.email });
    }
    return rows;
  }, [users, who]);

  /*
   * Disabled with the reason beside it rather than withheld: the refusal is
   * about THIS reader, so a missing button would read as a broken page. The
   * gate is the optimisation — `handleSave` still handles the 403, because a
   * grant can be withdrawn under a live access token.
   */
  /*
   * The why-disabled sentence rides on `title`, which `Button` turns into a
   * hover label on a wrapper span when the button is disabled (a disabled
   * button never receives the hover itself). SUPERSEDED 2026-10-11: a visible
   * paragraph under the button.
   */
  const newTaskButton = (
    <Button
      variant="primary"
      leadingIcon={<PlusIcon />}
      disabled={!canCreateTask}
      onClick={() => setFormState({})}
      {...(createTaskDenial ? { title: createTaskDenial } : {})}
    >
      New task
    </Button>
  );

  const pageActions = (
    <>
      <ViewToggle label="Task view" value={view} options={VIEW_OPTIONS} onChange={setView} />
      {newTaskButton}
    </>
  );

  /**
   * Shared by both views: the same compact row everywhere on this page.
   *
   * ⚠️ **ONE OBJECT, TWO MOUNTS — anything a row needs goes HERE.** The bucket
   * view and the table view both spread this, so a prop handed to one of them
   * directly would make the same task behave differently depending on which
   * view the reader happened to be in, with nothing failing.
   *
   * ⚠️ **"Open in ITSM" and the "From ITSM" chip deliberately add nothing to
   * this object**, and that absence is recorded so the next reader does not go
   * looking for the prop that wires them. Both are computed inside the row from
   * `task.itsm` and `task.origin` — so there is no per-mount answer to get
   * wrong, and `TaskList` stays presentational. A pass-through prop nothing
   * reads would be a dead prop, and the next person would keep it in step for
   * no return.
   *
   * ⚠️ *SUPERSEDED: "against the host and path that `lib/itsm.ts` owns" — that
   * file is DELETED (2026-10-13).* **No host is in this build at all now.** The
   * row renders `task.itsm.url`, which the API serves from `ITSM_APP_BASE_URL`
   * (corrected 2026-10-14; *superseded: `ITSM_BASE_URL`* — ITSM's API and its
   * web application are on different hosts, so the link has its own key); the
   * reason that moved is in `TaskItsmLink.url` in `lib/types.ts`. The clause
   * above is unaffected: both are still computed in the row, from the task.
   */
  const listProps = {
    showTarget: true,
    variant: 'compact' as const,
    now,
    onToggleState: (task: Task, next: TaskState) => void handleToggleState(task, next),
    onEdit: (task: Task) => setFormState({ task }),
    onDelete: (task: Task) => void handleDelete(task),
    busyTaskId,
    viewerTimeZone,
    viewerId,
    canUpdate: canUpdateTask,
    canDelete: canDeleteTask,
    writeDenialReason: updateTaskDenial,
  };

  /*
   * ⚠️ Below every hook, so the early return cannot change the hook order. The
   * refusal is rendered here rather than by the route, which carries no guard.
   */
  if (!canReadSurface) {
    return (
      <>
        <PageHeader title="Tasks" />
        <Panel className="min-w-0">
          <EmptyState
            title="This page isn’t available to your account"
            description="Your permission set does not include this. An administrator can change that."
          />
        </Panel>
      </>
    );
  }

  return (
    <>
      {/* AppShell provides `<main>` and the app bar but no title - without
          this the page has no `<h1>`. */}
      {/* The nav row reads "Tasks Tracker"; the page is simply "Tasks".
          SUPERSEDED 2026-10-11: eyebrow "Operations", title "My tasks". The
          view toggle and New task moved up here from a panel header, so the
          panel opens straight onto the work. */}
      <PageHeader title="Tasks" actions={pageActions} />
      <Panel aria-label={view === 'brief' ? 'My day' : 'All tasks'} className="min-w-0">
        {actionError && <ErrorBanner title="That didn&rsquo;t work.">{actionError}</ErrorBanner>}

        {view === 'brief' ? (
          <>
            {/*
              * ONE bar: the four counts as jump links, and the window that
              * defines Upcoming. EXACT counts from the brief's own query, never
              * `overdue.length` (rule 1). Overdue is toned danger when non-zero
              * AND carries its word — never colour alone.
              */}
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-border px-4 py-2">
              <nav aria-label="Jump to a group" className="min-w-0">
                {counts ? (
                  <ul className="m-0 flex list-none flex-wrap items-center gap-x-1 gap-y-1 p-0">
                    {BUCKETS.map((bucket, index) => {
                      const count = counts[bucket.key];
                      const alarming = bucket.key === 'overdue' && count > 0;
                      return (
                        <Fragment key={bucket.key}>
                          {index > 0 && (
                            <li aria-hidden="true" className="text-fg-subtle">
                              &middot;
                            </li>
                          )}
                          <li>
                            <button
                              type="button"
                              onClick={() => jumpToGroup(bucket.key)}
                              className="inline-flex items-baseline gap-1 rounded-xs px-1.5 py-1 text-sm hover:bg-surface-hover max-sm:py-2"
                            >
                              <span className={alarming ? 'font-medium text-danger' : 'text-fg-secondary'}>
                                {bucket.label}
                              </span>
                              <span
                                className={cx(
                                  'font-semibold tabular',
                                  alarming ? 'text-danger' : 'text-fg',
                                )}
                              >
                                {count}
                              </span>
                            </button>
                          </li>
                        </Fragment>
                      );
                    })}
                  </ul>
                ) : (
                  <Skeleton className="h-5 w-64 max-w-full" />
                )}
              </nav>
              {/* No "everyone" control here, deliberately (rule 2). */}
              <div className="flex min-w-0 items-center gap-2">
                <label htmlFor="tasks-horizon" className="shrink-0 text-xs text-fg-muted">
                  Upcoming
                </label>
                <div className="w-36 min-w-0">
                  {/* No `emptyOptionLabel`: there is no "any window" — the
                      bucket is always some number of days. */}
                  <Combobox
                    id="tasks-horizon"
                    value={String(horizonDays)}
                    onChange={(value) => setParam('horizon', value)}
                    options={horizonOptions}
                    searchPlaceholder="Search windows…"
                  />
                </div>
              </div>
            </div>

            {briefError && (
              <ErrorBanner title="Couldn&rsquo;t load your day.">{briefError}</ErrorBanner>
            )}

            {briefInitialLoad ? (
              <div className="grid gap-2 px-4 py-4">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-8 rounded-md" />
                <Skeleton className="h-8 rounded-md" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-8 rounded-md" />
              </div>
            ) : !brief || !counts ? (
              !briefError && (
                <EmptyState title="Nothing to show" />
              )
            ) : (
              <div
                aria-busy={briefLoading || undefined}
                className={cx(
                  'transition-opacity duration-150 ease-out motion-reduce:transition-none',
                  briefLoading && 'opacity-60',
                )}
              >
                <p role="status" aria-live="polite" className="sr-only">
                  {`${counts.total} ${counts.total === 1 ? 'task' : 'tasks'}, ${counts.overdue} overdue`}
                </p>

                {/* Kept, and only when large: past a few minutes the skew
                    decides which tasks read as late (rule 7). */}
                {Math.abs(skewMinutes) >= SKEW_WARNING_MINUTES && (
                  <p className="m-0 border-b border-border px-4 py-1.5 text-xs text-fg-muted">
                    Your clock is {Math.abs(skewMinutes)} min{' '}
                    {skewMinutes > 0 ? 'behind' : 'ahead of'} the server; overdue uses server time.
                  </p>
                )}

                {counts.total === 0 ? (
                  <EmptyState
                    title="Nothing outstanding"
                    action={
                      closedCount !== null && closedCount > 0 ? (
                        <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-center">
                          <Button variant="secondary" onClick={showClosedTasks}>
                            {`See ${closedCount} completed`}
                          </Button>
                          {newTaskButton}
                        </div>
                      ) : (
                        newTaskButton
                      )
                    }
                  />
                ) : (
                  BUCKETS.map((bucket) => {
                    const tasks = brief[bucket.key];
                    const count = counts[bucket.key];
                    // Capped list vs exact count (rule 1): a terse "+N more"
                    // rather than a short list passing for the whole group.
                    // SUPERSEDED 2026-10-11: a warning notice saying so.
                    const hidden = count - tasks.length;
                    return (
                      <section
                        key={bucket.key}
                        aria-labelledby={groupHeadingId(bucket.key)}
                        className="scroll-mt-20"
                      >
                        <div className="flex items-baseline gap-2 border-b border-border bg-surface-sunken px-4 py-1">
                          <h3
                            id={groupHeadingId(bucket.key)}
                            tabIndex={-1}
                            className={cx(
                              'm-0 scroll-mt-20 text-xs font-semibold',
                              bucket.key === 'overdue' && count > 0
                                ? 'text-danger'
                                : 'text-fg-secondary',
                            )}
                          >
                            {bucket.label}
                          </h3>
                          <span className="text-xs text-fg-muted tabular">{count}</span>
                          {hidden > 0 && (
                            <span className="text-2xs text-fg-subtle tabular">+{hidden} more</span>
                          )}
                        </div>
                        {tasks.length === 0 ? (
                          <p className="m-0 px-4 py-2 text-xs text-fg-muted">{bucket.empty}</p>
                        ) : (
                          <TaskList tasks={tasks} {...listProps} />
                        )}
                      </section>
                    );
                  })
                )}
              </div>
            )}
          </>
        ) : (
          <>
            {/* Its own full-width row rather than PanelHeader's `actions` slot
                (shrink-0, cannot wrap). Every track is `min-w-0` and full
                width below `sm`, so 320px is a single stack. */}
            <div className="flex flex-wrap items-end gap-x-2 gap-y-2 border-b border-border px-4 py-2">
              <div className="w-[calc(50%-0.25rem)] min-w-0 sm:w-40">
                {/* ONE control over `state` and `closed` (rule 9). */}
                <Field label="Status">
                  {({ id }) => (
                    // Every state ticked reads as the empty selection, "Any
                    // status", which writes `status=all` (absent is open only).
                    <MultiCombobox
                      id={id}
                      values={statuses.length === TASK_STATES.length ? [] : statuses}
                      onChange={setStatuses}
                      options={STATUS_OPTIONS}
                      placeholder={ANY_STATUS_LABEL}
                      searchPlaceholder="Search statuses…"
                      summaryNoun={{ singular: 'status', plural: 'statuses' }}
                      maxVisibleChips={1}
                    />
                  )}
                </Field>
              </div>

              <div className="w-[calc(50%-0.25rem)] min-w-0 sm:w-36">
                <Field label="Kind">
                  {({ id }) => (
                    <MultiCombobox
                      id={id}
                      values={kinds}
                      onChange={setKinds}
                      options={KIND_OPTIONS}
                      placeholder="Any kind"
                      searchPlaceholder="Search kinds…"
                      summaryNoun={{ singular: 'kind', plural: 'kinds' }}
                      maxVisibleChips={1}
                    />
                  )}
                </Field>
              </div>

              {/* Gated on the GRANT (rule 3/4) - without it, offering this
                  control would only offer a guaranteed 403. */}
              {hasAudit && (
                <div className="w-[calc(50%-0.25rem)] min-w-0 sm:w-48">
                  {/* Mine and Everyone's are static, so a dead `/users` read
                      leaves a control that still WORKS and just appears to
                      know no colleagues - the error text says so; the invalid
                      border is a second signal, never the only one. Kept,
                      shortened 2026-10-11. */}
                  <Field
                    label="Whose tasks"
                    {...(usersFailed
                      ? { error: 'Staff list unavailable; Mine and Everyone’s still work.' }
                      : {})}
                  >
                    {({ id, 'aria-describedby': describedBy }) => (
                      /*
                       * 🔴 **The one control on this page where search is
                       * load-bearing rather than cosmetic** — up to ~103 rows
                       * (two static, one injected, one page of `/users` at the
                       * API's ceiling of 100), where every other filter here is
                       * five to seven.
                       *
                       * ⚠️ **`loading`, NOT `disabled`.** *SUPERSEDED
                       * 2026-09-13: `disabled={usersLoading}`, which a native
                       * `<Select>` needed because an unarrived list left it
                       * showing one option and nothing to choose.* A `Combobox`
                       * has a loading state of its own INSIDE the popover, and
                       * disabling the trigger would take `Mine` and
                       * `Everyone's tasks` away for the duration — the two
                       * choices that never needed `/users` and that the failure
                       * copy beside this control promises keep working. A
                       * control that is dead while a list it does not need
                       * arrives is the same wrong answer as one that is dead
                       * when that list never does.
                       */
                      <MultiCombobox
                        id={id}
                        values={who}
                        onChange={setWho}
                        options={whoOptions}
                        aria-describedby={describedBy}
                        placeholder="Everyone’s tasks"
                        searchPlaceholder="Search people…"
                        summaryNoun={{ singular: 'person', plural: 'people' }}
                        maxVisibleChips={1}
                        loading={usersLoading}
                        /*
                         * A fact about the DATA, not advice — the documented
                         * exception to the no-helper-text rule, and the same
                         * shape the Projects picker on the sales tracker and
                         * the projects-of-interest cell on `/companies` use.
                         *
                         * ⚠️ **It says the search is capped, not just the
                         * list.** Filtering is local to what is loaded, so a
                         * colleague past the cap is not merely further down —
                         * typing their name finds nothing at all, which reads
                         * as "this person has no tasks". Naming the cap is the
                         * only thing on screen that separates those two.
                         */
                        {...(moreUsers
                          ? {
                              footnote: `Showing the first ${users.length} colleagues; search covers only these.`,
                            }
                          : {})}
                        {...(usersFailed ? { invalid: true } : {})}
                      />
                    )}
                  </Field>
                </div>
              )}

              <div className="w-[calc(50%-0.25rem)] min-w-0 sm:w-44">
                <Field label="Order">
                  {({ id }) => (
                    <Combobox
                      id={id}
                      // A half-set pair is not a choice: only both keys
                      // together name a row, and anything else is the empty
                      // value, which sends neither.
                      value={sort && order ? `${sort}:${order}` : ''}
                      onChange={setSortChoice}
                      options={SORT_CHOICES}
                      emptyOptionLabel={DEFAULT_ORDER_LABEL}
                      placeholder={DEFAULT_ORDER_LABEL}
                      searchPlaceholder="Search orderings…"
                    />
                  )}
                </Field>
              </div>

              <div className="min-w-0 pb-2">
                <Checkbox
                  label="Personal to-dos only"
                  checked={unattached}
                  onChange={(e) => setParam('unattached', e.target.checked ? '1' : '')}
                />
              </div>

              <div className="min-w-0 pb-2">
                <Checkbox
                  label="Show upcoming"
                  checked={includeUpcoming}
                  onChange={(e) => setParam('upcoming', e.target.checked ? '1' : '')}
                />
              </div>

              {anyListFilter && (
                <div className="min-w-0 pb-1">
                  <Button variant="ghost" size="sm" onClick={clearListFilters}>
                    Clear
                  </Button>
                </div>
              )}
            </div>

            {/* SUPERSEDED 2026-10-11: a "Your own queue: assigned to you, or
                asked by you." line for readers without the audit grant. */}

            {listForbidden && (
              <Notice
                tone="danger"
                role="alert"
                title={<>You can&rsquo;t see that queue.</>}
                className="mx-4 my-3"
                action={
                  <Button variant="secondary" size="sm" onClick={() => setParam('who', 'me')}>
                    Show my tasks
                  </Button>
                }
              >
                <span className="min-w-0">
                  Needs the tasks audit grant.
                </span>
              </Notice>
            )}

            {listError && <ErrorBanner title="Couldn&rsquo;t load tasks.">{listError}</ErrorBanner>}

            <p role="status" aria-live="polite" className="sr-only">
              {listDescription}
            </p>

            <div
              aria-busy={listLoading || undefined}
              className={cx(
                'transition-opacity duration-150 ease-out motion-reduce:transition-none',
                listLoading && result && 'opacity-60',
              )}
            >
              {listInitialLoad ? (
                <ul className="min-w-0">
                  {Array.from({ length: LIST_SKELETON_ROWS }, (_unused, index) => (
                    <li
                      key={index}
                      className="flex min-w-0 items-center gap-2 border-t border-border px-3 py-2.5 first:border-t-0"
                    >
                      <Skeleton className="h-4 w-4 rounded-xs" />
                      <Skeleton className="h-4 min-w-0 flex-1" />
                      <Skeleton className="h-4 w-12" />
                    </li>
                  ))}
                </ul>
              ) : rows.length === 0 ? (
                listForbidden ? null : (
                  <EmptyState
                    title={anyListFilter ? 'No tasks match' : 'No tasks yet'}
                    action={
                      anyListFilter ? (
                        <Button variant="secondary" size="sm" onClick={clearListFilters}>
                          Clear filters
                        </Button>
                      ) : (
                        newTaskButton
                      )
                    }
                  />
                )
              ) : (
                <TaskList tasks={rows} {...listProps} />
              )}
            </div>

            {result && total > 0 && (
              <PanelFooter>
                <p className="m-0 text-xs text-fg-muted tabular">
                  {`${firstRow}–${lastRow} of ${total}`}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    className="flex-1 sm:flex-none"
                    leadingIcon={<ChevronIcon direction="left" />}
                    disabled={page <= 1}
                    onClick={() => setPage((current) => current - 1)}
                  >
                    Prev
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="flex-1 sm:flex-none"
                    trailingIcon={<ChevronIcon direction="right" />}
                    disabled={page >= totalPages}
                    onClick={() => setPage((current) => current + 1)}
                  >
                    Next
                  </Button>
                </div>
              </PanelFooter>
            )}
          </>
        )}
      </Panel>

      {/*
        * No `target`: a task raised from this page is a personal to-do, which is
        * legitimate here - unlike a note, which must attach to exactly one record.
        */}
      <TaskFormModal
        open={formState !== null}
        onClose={() => setFormState(null)}
        {...(formState?.task ? { task: formState.task } : {})}
        onSaved={handleSaved}
      />
    </>
  );
}
